/**
 * ImageUploader Component
 * Modal dialog for uploading new images to DAM
 * Features:
 * - Drag and drop area
 * - File input for selecting images
 * - Upload progress indicator
 * - Supported formats: PNG, JPG, WEBP, GIF
 * - Max file size indicator
 */

import { useState, useCallback, useRef } from 'react'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import {
  Upload,
  Image as ImageIcon,
  X,
  CheckCircle2,
  AlertCircle,
  Loader2,
  FileImage,
} from 'lucide-react'
import { assetService } from '@/lib/api/services/assets'

// ============================================
// Types
// ============================================

export interface ImageUploaderProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onUploadComplete: (asset: {
    id: string
    url: string
    thumbnailUrl?: string
    name: string
    width?: number
    height?: number
    fileSize: number
    mimeType: string
  }) => void
}

type UploadStatus = 'idle' | 'selected' | 'uploading' | 'success' | 'error'

interface FilePreview {
  file: File
  preview: string
  width?: number
  height?: number
}

// ============================================
// Constants
// ============================================

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif']
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
const ACCEPTED_EXTENSIONS = '.png, .jpg, .jpeg, .webp, .gif'

// ============================================
// Helper Functions
// ============================================

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight })
      URL.revokeObjectURL(img.src)
    }
    img.onerror = () => {
      reject(new Error('Failed to load image'))
      URL.revokeObjectURL(img.src)
    }
    img.src = URL.createObjectURL(file)
  })
}

// ============================================
// ImageUploader Component
// ============================================

export function ImageUploader({
  open,
  onOpenChange,
  onUploadComplete,
}: ImageUploaderProps) {
  const [status, setStatus] = useState<UploadStatus>('idle')
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [filePreview, setFilePreview] = useState<FilePreview | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Reset state when dialog closes
  const handleOpenChange = useCallback((newOpen: boolean) => {
    if (!newOpen) {
      setStatus('idle')
      setProgress(0)
      setError(null)
      if (filePreview) {
        URL.revokeObjectURL(filePreview.preview)
      }
      setFilePreview(null)
    }
    onOpenChange(newOpen)
  }, [onOpenChange, filePreview])

  // Validate file
  const validateFile = (file: File): string | null => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return `Invalid file type. Accepted formats: ${ACCEPTED_EXTENSIONS}`
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File is too large. Maximum size is ${formatFileSize(MAX_FILE_SIZE)}`
    }
    return null
  }

  // Handle file selection
  const handleFileSelect = useCallback(async (file: File) => {
    // Validate
    const validationError = validateFile(file)
    if (validationError) {
      setError(validationError)
      setStatus('error')
      return
    }

    // Get image dimensions
    try {
      const dimensions = await getImageDimensions(file)
      const preview = URL.createObjectURL(file)

      setFilePreview({
        file,
        preview,
        width: dimensions.width,
        height: dimensions.height,
      })
      setStatus('selected')
      setError(null)
    } catch {
      setError('Failed to load image preview')
      setStatus('error')
    }
  }, [])

  // Handle drag events
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const files = e.dataTransfer.files
    const firstFile = files[0]
    if (files && files.length > 0 && firstFile) {
      handleFileSelect(firstFile)
    }
  }, [handleFileSelect])

  // Handle file input change
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    const firstFile = files?.[0]
    if (files && files.length > 0 && firstFile) {
      handleFileSelect(firstFile)
    }
  }, [handleFileSelect])

  // Handle upload
  const handleUpload = useCallback(async () => {
    if (!filePreview) return

    setStatus('uploading')
    setProgress(0)
    setError(null)

    try {
      // Try real upload first
      try {
        const result = await assetService.uploadAsset(
          { file: filePreview.file },
          (progress) => setProgress(progress)
        )

        setStatus('success')
        setProgress(100)

        // Notify parent
        setTimeout(() => {
          onUploadComplete({
            id: result.id,
            url: result.url,
            thumbnailUrl: result.thumbnailUrl,
            name: result.name,
            width: filePreview.width,
            height: filePreview.height,
            fileSize: filePreview.file.size,
            mimeType: filePreview.file.type,
          })
        }, 500)
        return
      } catch {
        // API not available, simulate upload for development
      }

      // Simulate upload for development
      for (let i = 0; i <= 100; i += 10) {
        await new Promise(resolve => setTimeout(resolve, 100))
        setProgress(i)
      }

      setStatus('success')
      setProgress(100)

      // Notify parent with mock data
      setTimeout(() => {
        onUploadComplete({
          id: `uploaded-${Date.now()}`,
          url: filePreview.preview,
          thumbnailUrl: filePreview.preview,
          name: filePreview.file.name,
          width: filePreview.width,
          height: filePreview.height,
          fileSize: filePreview.file.size,
          mimeType: filePreview.file.type,
        })
      }, 500)
    } catch (err) {
      setStatus('error')
      setError(err instanceof Error ? err.message : 'Upload failed')
    }
  }, [filePreview, onUploadComplete])

  // Clear selected file
  const handleClear = useCallback(() => {
    if (filePreview) {
      URL.revokeObjectURL(filePreview.preview)
    }
    setFilePreview(null)
    setStatus('idle')
    setError(null)
    setProgress(0)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [filePreview])

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-purple-500" />
            Upload Image
          </DialogTitle>
          <DialogDescription>
            Upload a new image to your digital asset library
          </DialogDescription>
        </DialogHeader>

        {/* Drop Zone or Preview */}
        <div className="py-4">
          {!filePreview ? (
            /* Drop Zone */
            <div
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={cn(
                'relative border-2 border-dashed rounded-xl p-8',
                'text-center cursor-pointer transition-all',
                'hover:border-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/10',
                isDragging
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                  : 'border-neutral-300 dark:border-neutral-700',
                status === 'error' && 'border-red-400 bg-red-50 dark:bg-red-900/10'
              )}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_EXTENSIONS}
                onChange={handleInputChange}
                className="hidden"
              />

              <div className="flex flex-col items-center gap-3">
                <div className={cn(
                  'p-4 rounded-full',
                  isDragging
                    ? 'bg-purple-100 dark:bg-purple-900/30'
                    : 'bg-neutral-100 dark:bg-neutral-800'
                )}>
                  <FileImage className={cn(
                    'h-8 w-8',
                    isDragging ? 'text-purple-500' : 'text-neutral-400'
                  )} />
                </div>

                <div>
                  <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                    {isDragging ? 'Drop image here' : 'Drag and drop an image'}
                  </p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                    or click to browse
                  </p>
                </div>

                <div className="text-xs text-neutral-400 dark:text-neutral-500">
                  <p>PNG, JPG, WEBP, GIF up to {formatFileSize(MAX_FILE_SIZE)}</p>
                </div>
              </div>
            </div>
          ) : (
            /* File Preview */
            <div className="space-y-4">
              <div className="relative rounded-xl overflow-hidden border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50">
                <div className="aspect-video relative">
                  <img
                    src={filePreview.preview}
                    alt="Preview"
                    className="w-full h-full object-contain"
                  />
                </div>

                {/* Clear button (only when not uploading) */}
                {status !== 'uploading' && status !== 'success' && (
                  <button
                    type="button"
                    onClick={handleClear}
                    className="absolute top-2 right-2 p-1.5 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}

                {/* Upload Progress Overlay */}
                {status === 'uploading' && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <div className="text-center">
                      <Loader2 className="h-8 w-8 text-white animate-spin mx-auto mb-2" />
                      <p className="text-white text-sm font-medium">Uploading...</p>
                    </div>
                  </div>
                )}

                {/* Success Overlay */}
                {status === 'success' && (
                  <div className="absolute inset-0 bg-green-500/80 flex items-center justify-center">
                    <div className="text-center">
                      <CheckCircle2 className="h-10 w-10 text-white mx-auto mb-2" />
                      <p className="text-white text-sm font-medium">Upload Complete!</p>
                    </div>
                  </div>
                )}
              </div>

              {/* File Info */}
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 text-neutral-600 dark:text-neutral-400 min-w-0">
                  <ImageIcon className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">{filePreview.file.name}</span>
                </div>
                <div className="flex items-center gap-3 text-neutral-500 dark:text-neutral-400 text-xs flex-shrink-0">
                  {filePreview.width && filePreview.height && (
                    <span>{filePreview.width} x {filePreview.height}</span>
                  )}
                  <span>{formatFileSize(filePreview.file.size)}</span>
                </div>
              </div>

              {/* Progress Bar */}
              {(status === 'uploading' || status === 'success') && (
                <div className="space-y-1">
                  <Progress value={progress} className="h-2" />
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 text-right">
                    {progress}%
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mt-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <p className="text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={status === 'uploading'}
            className="rounded-xl"
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!filePreview || status === 'uploading' || status === 'success'}
            className="rounded-xl"
          >
            {status === 'uploading' ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-1.5" />
                Upload Image
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ImageUploader
